import type {
  ReportGenerationAuthorityFieldMetadata,
  ReportGenerationAuthorityScope,
} from "./reportGenerationFieldAuthority";
import {
  REPORT_GENERATION_ENTITIES_BY_ID,
  REPORT_GENERATION_ENTITY_JOINS,
  REPORT_GENERATION_MODULE_COLORS,
  type ReportGenerationCognitoProfile,
  type ReportGenerationEntityAccess,
  type ReportGenerationEntityDefinition,
  type ReportGenerationFieldDefinition,
  type ReportGenerationShellPersona,
  type ReportGenerationTemplateAccess,
  type ReportGenerationTemplateDefinition,
  resolveReportGenerationEntityAccess,
  resolveReportGenerationPermission,
  resolveReportGenerationTemplateAccess,
} from "./reportGenerationSemanticCatalog";

export type ReportGenerationFilterCondition = {
  id: string;
  fieldId: string;
  fieldLabel: string;
  operator: string;
  value: string;
  fieldType: ReportGenerationFieldDefinition["type"];
};

export type ReportGenerationSortConfig = {
  fieldId: string;
  fieldLabel: string;
  direction: "asc" | "desc";
};

export type ReportGenerationFilterState = {
  selectedEntities: string[];
  selectedFields: string[];
  conditions: ReportGenerationFilterCondition[];
  dateRange: {
    preset: string;
    from: string;
    to: string;
  };
  sorts: ReportGenerationSortConfig[];
};

export type ReportGenerationFieldOption = {
  entityId: string;
  entityLabel: string;
  fieldId: string;
  fieldLabel: string;
  fieldType: ReportGenerationFieldDefinition["type"];
  allowedOperators?: ReportGenerationFieldDefinition["allowedOperators"];
  sortable?: boolean;
  moduleId: ReportGenerationEntityDefinition["moduleId"];
  moduleLabel: ReportGenerationEntityDefinition["moduleLabel"];
  authority?: ReportGenerationAuthorityFieldMetadata;
};

export type ReportGenerationEntityViewModel = ReportGenerationEntityAccess & {
  selected: boolean;
  matchesSearch: boolean;
  visibleCategories: Array<{
    name: string;
    fields: Array<
      ReportGenerationFieldDefinition & {
        selected: boolean;
        matchesSearch: boolean;
        locked: boolean;
      }
    >;
  }>;
};

export type ReportGenerationTemplateViewModel =
  ReportGenerationTemplateAccess & {
    selected: boolean;
  };

export type ReportGenerationModuleViewModel = {
  moduleId: ReportGenerationEntityDefinition["moduleId"];
  moduleLabel: ReportGenerationEntityDefinition["moduleLabel"];
  routeRoot: string;
  color: string;
  entities: ReportGenerationEntityViewModel[];
};

export type ReportGenerationFilterViewModel = {
  state: ReportGenerationFilterState;
  activeFilterCount: number;
  canRunReport: boolean;
  joins: string[];
  availableFields: ReportGenerationFieldOption[];
  selectedFieldOptions: ReportGenerationFieldOption[];
  modules: ReportGenerationModuleViewModel[];
  inaccessibleEntities: ReportGenerationEntityViewModel[];
  templates: {
    available: ReportGenerationTemplateViewModel[];
    blocked: ReportGenerationTemplateViewModel[];
  };
};

const DEFAULT_DATE_PRESET = "Custom";
const TEMPLATE_DATE_PRESET = "This Year";
const DEFAULT_FIELD_OPERATOR: Record<
  ReportGenerationFieldDefinition["type"],
  string
> = {
  text: "equals",
  number: "equals",
  date: "before",
  enum: "is",
  boolean: "is_true",
};

const normalizeText = (value: string | null | undefined): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const matchesSearch = (candidate: string, query: string): boolean =>
  normalizeText(candidate).includes(normalizeText(query));

export const createReportGenerationFilterState =
  (): ReportGenerationFilterState => ({
    selectedEntities: [],
    selectedFields: [],
    conditions: [],
    dateRange: {
      preset: DEFAULT_DATE_PRESET,
      from: "",
      to: "",
    },
    sorts: [],
  });

export const createReportGenerationCondition = (
  field: ReportGenerationFieldOption,
): ReportGenerationFilterCondition => ({
  id: `cond-${field.fieldId}`,
  fieldId: field.fieldId,
  fieldLabel: field.fieldLabel,
  operator:
    field.allowedOperators?.[0] ?? DEFAULT_FIELD_OPERATOR[field.fieldType],
  value: "",
  fieldType: field.fieldType,
});

export const toggleReportGenerationEntity = (
  state: ReportGenerationFilterState,
  entityId: string,
): ReportGenerationFilterState => {
  const entity = REPORT_GENERATION_ENTITIES_BY_ID[entityId];
  if (!entity) {
    return state;
  }
  const entityFieldIds = entity.categories.flatMap((category) =>
    category.fields.map((field) => field.id),
  );
  const selectedEntities = state.selectedEntities.includes(entityId)
    ? state.selectedEntities.filter((current) => current !== entityId)
    : [...state.selectedEntities, entityId];
  const selectedFields = state.selectedEntities.includes(entityId)
    ? state.selectedFields.filter(
        (fieldId) => !entityFieldIds.includes(fieldId),
      )
    : state.selectedFields;
  const conditions = state.selectedEntities.includes(entityId)
    ? state.conditions.filter(
        (condition) => !entityFieldIds.includes(condition.fieldId),
      )
    : state.conditions;
  const sorts = state.selectedEntities.includes(entityId)
    ? state.sorts.filter((sort) => !entityFieldIds.includes(sort.fieldId))
    : state.sorts;

  return {
    ...state,
    selectedEntities,
    selectedFields,
    conditions,
    sorts,
  };
};

export const toggleReportGenerationField = (
  state: ReportGenerationFilterState,
  fieldId: string,
): ReportGenerationFilterState => ({
  ...state,
  selectedFields: state.selectedFields.includes(fieldId)
    ? state.selectedFields.filter((current) => current !== fieldId)
    : [...state.selectedFields, fieldId],
});

export const applyReportGenerationTemplate = (
  template: Pick<
    ReportGenerationTemplateDefinition,
    "entities" | "fields" | "conditions" | "sorts"
  >,
): ReportGenerationFilterState => ({
  selectedEntities: [...template.entities],
  selectedFields: [...template.fields],
  conditions: template.conditions?.map((condition) => ({ ...condition })) ?? [],
  dateRange: {
    preset: TEMPLATE_DATE_PRESET,
    from: "",
    to: "",
  },
  sorts: template.sorts?.map((sort) => ({ ...sort })) ?? [],
});

export const addReportGenerationCondition = (
  state: ReportGenerationFilterState,
  field: ReportGenerationFieldOption | null,
): ReportGenerationFilterState => {
  if (!field) {
    return state;
  }
  return {
    ...state,
    conditions: [...state.conditions, createReportGenerationCondition(field)],
  };
};

export const updateReportGenerationCondition = (
  state: ReportGenerationFilterState,
  conditionId: string,
  updates: Partial<ReportGenerationFilterCondition>,
): ReportGenerationFilterState => ({
  ...state,
  conditions: state.conditions.map((condition) =>
    condition.id === conditionId ? { ...condition, ...updates } : condition,
  ),
});

export const removeReportGenerationCondition = (
  state: ReportGenerationFilterState,
  conditionId: string,
): ReportGenerationFilterState => ({
  ...state,
  conditions: state.conditions.filter(
    (condition) => condition.id !== conditionId,
  ),
});

export const updateReportGenerationDateRange = (
  state: ReportGenerationFilterState,
  updates: Partial<ReportGenerationFilterState["dateRange"]>,
): ReportGenerationFilterState => ({
  ...state,
  dateRange: {
    ...state.dateRange,
    ...updates,
  },
});

export const setReportGenerationPrimarySort = (
  state: ReportGenerationFilterState,
  field: ReportGenerationFieldOption | null,
): ReportGenerationFilterState => ({
  ...state,
  sorts: field
    ? [
        {
          fieldId: field.fieldId,
          fieldLabel: field.fieldLabel,
          direction: state.sorts[0]?.direction ?? "asc",
        },
      ]
    : [],
});

export const toggleReportGenerationPrimarySortDirection = (
  state: ReportGenerationFilterState,
): ReportGenerationFilterState => {
  if (!state.sorts[0]) {
    return state;
  }
  return {
    ...state,
    sorts: [
      {
        ...state.sorts[0],
        direction: state.sorts[0].direction === "asc" ? "desc" : "asc",
      },
    ],
  };
};

export const clearReportGenerationFilters = (): ReportGenerationFilterState =>
  createReportGenerationFilterState();

export const deriveReportGenerationFieldOptions = (
  entities: readonly ReportGenerationEntityAccess[],
  selectedEntityIds: readonly string[],
  options?: {
    isFieldAccessible?: (
      entity: ReportGenerationEntityAccess,
      field: ReportGenerationFieldDefinition,
    ) => boolean;
  },
): ReportGenerationFieldOption[] =>
  entities
    .filter(
      (entity) => entity.accessible && selectedEntityIds.includes(entity.id),
    )
    .flatMap((entity) =>
      entity.categories.flatMap((category) =>
        category.fields
          .filter(
            (field) =>
              field.selectable !== false &&
              (options?.isFieldAccessible?.(entity, field) ?? true),
          )
          .map((field) => ({
            entityId: entity.id,
            entityLabel: entity.label,
            fieldId: field.id,
            fieldLabel: field.label,
            fieldType: field.type,
            allowedOperators:
              field.allowedOperators ?? field.authority?.allowedOperators,
            sortable: field.sortable ?? field.authority?.sortSupported ?? true,
            moduleId: entity.moduleId,
            moduleLabel: entity.moduleLabel,
            authority: field.authority,
          })),
      ),
    );

const hasActiveDateRange = (state: ReportGenerationFilterState): boolean =>
  state.dateRange.preset !== DEFAULT_DATE_PRESET ||
  Boolean(state.dateRange.from) ||
  Boolean(state.dateRange.to);

export const deriveReportGenerationActiveFilterCount = (
  state: ReportGenerationFilterState,
): number => state.conditions.length + (hasActiveDateRange(state) ? 1 : 0);

export const deriveReportGenerationJoinIndicators = (
  selectedEntityIds: readonly string[],
): string[] => {
  const indicators: string[] = [];
  for (
    let leftIndex = 0;
    leftIndex < selectedEntityIds.length;
    leftIndex += 1
  ) {
    for (
      let rightIndex = leftIndex + 1;
      rightIndex < selectedEntityIds.length;
      rightIndex += 1
    ) {
      const leftId = selectedEntityIds[leftIndex];
      const rightId = selectedEntityIds[rightIndex];
      if (!leftId || !rightId) {
        continue;
      }
      const joinLabel =
        REPORT_GENERATION_ENTITY_JOINS[leftId]?.[rightId] ??
        REPORT_GENERATION_ENTITY_JOINS[rightId]?.[leftId];
      if (!joinLabel) {
        continue;
      }
      const leftEntity = REPORT_GENERATION_ENTITIES_BY_ID[leftId];
      const rightEntity = REPORT_GENERATION_ENTITIES_BY_ID[rightId];
      if (!leftEntity || !rightEntity) {
        continue;
      }
      indicators.push(
        `${leftEntity.label} ↔ ${rightEntity.label} linked by ${joinLabel}`,
      );
    }
  }
  return indicators;
};

export const buildReportGenerationFilterViewModel = (input: {
  profile: ReportGenerationCognitoProfile | null;
  effectivePersona: ReportGenerationShellPersona;
  state: ReportGenerationFilterState;
  entitySearch: string;
  selectedTemplateId?: string | null;
  authorityScope?: ReportGenerationAuthorityScope | "all";
  includeLegacy?: boolean;
}): ReportGenerationFilterViewModel => {
  const authorityScope = input.authorityScope ?? "all";
  const includeLegacy = input.includeLegacy ?? true;
  const entityAccess = resolveReportGenerationEntityAccess({
    profile: input.profile,
    effectivePersona: input.effectivePersona,
  }).filter((entity) => {
    if (authorityScope === "all") {
      return includeLegacy || entity.authorityStatus !== "legacy-unverified";
    }
    return (
      entity.authorityScope === authorityScope &&
      entity.authorityStatus === "backed"
    );
  });
  const accessibleScopedEntityIds = new Set(
    entityAccess
      .filter((entity) => entity.accessible)
      .map((entity) => entity.id),
  );
  const selectedInaccessibleEntityIds = input.state.selectedEntities.filter(
    (entityId) => !accessibleScopedEntityIds.has(entityId),
  );
  const isFieldAccessible = (
    entity: ReportGenerationEntityAccess,
    field: ReportGenerationFieldDefinition,
  ): boolean =>
    resolveReportGenerationPermission(
      field.authority?.permission ?? entity.permission,
      {
        profile: input.profile,
        effectivePersona: input.effectivePersona,
      },
    ).granted;
  const availableFields = deriveReportGenerationFieldOptions(
    entityAccess,
    input.state.selectedEntities,
    { isFieldAccessible },
  );
  const availableFieldIds = new Set(
    availableFields.map((field) => field.fieldId),
  );
  const selectedFieldOptions = availableFields.filter((field) =>
    input.state.selectedFields.includes(field.fieldId),
  );
  const query = normalizeText(input.entitySearch);

  const entityViewModels = entityAccess.map<ReportGenerationEntityViewModel>(
    (entity) => {
      const visibleCategories = entity.categories
        .map((category) => ({
          name: category.name,
          fields: category.fields
            .filter(
              (field) =>
                field.selectable !== false && isFieldAccessible(entity, field),
            )
            .map((field) => ({
              ...field,
              selected: input.state.selectedFields.includes(field.id),
              locked: entity.fixedFieldSelection === true,
              matchesSearch:
                !query ||
                matchesSearch(field.label, query) ||
                matchesSearch(category.name, query) ||
                matchesSearch(entity.label, query),
            }))
            .filter((field) => !query || field.matchesSearch),
        }))
        .filter((category) => category.fields.length > 0);

      return {
        ...entity,
        selected: input.state.selectedEntities.includes(entity.id),
        matchesSearch:
          !query ||
          matchesSearch(entity.label, query) ||
          matchesSearch(entity.moduleLabel, query) ||
          visibleCategories.length > 0,
        visibleCategories,
      };
    },
  );

  const accessibleEntities = entityViewModels.filter(
    (entity) => entity.accessible && entity.matchesSearch,
  );
  const inaccessibleEntities = entityViewModels.filter(
    (entity) => !entity.accessible && entity.matchesSearch,
  );

  const modules = accessibleEntities.reduce<
    Record<string, ReportGenerationModuleViewModel>
  >((current, entity) => {
    const key = entity.moduleId;
    const existing = current[key];
    if (existing) {
      existing.entities.push(entity);
      return current;
    }
    current[key] = {
      moduleId: entity.moduleId,
      moduleLabel: entity.moduleLabel,
      routeRoot: entity.routeRoot,
      color: REPORT_GENERATION_MODULE_COLORS[entity.moduleLabel] ?? "#9ca3af",
      entities: [entity],
    };
    return current;
  }, {});

  const scopedEntityIds = new Set(entityAccess.map((entity) => entity.id));
  const scopedFieldIds = new Set(
    entityAccess.flatMap((entity) =>
      entity.categories.flatMap((category) =>
        category.fields
          .filter(
            (field) =>
              field.selectable !== false && isFieldAccessible(entity, field),
          )
          .map((field) => field.id),
      ),
    ),
  );
  const templateMatchesAuthorityScope = (
    template: ReportGenerationTemplateAccess,
  ): boolean => {
    if (authorityScope === "all") {
      return includeLegacy || template.authorityStatus !== "legacy-unverified";
    }
    return (
      template.authorityScope === authorityScope &&
      template.authorityStatus === "backed" &&
      template.entities.every((entityId) => scopedEntityIds.has(entityId)) &&
      template.fields.every((fieldId) => scopedFieldIds.has(fieldId))
    );
  };

  const templateViewModels = resolveReportGenerationTemplateAccess({
    profile: input.profile,
    effectivePersona: input.effectivePersona,
  }).map<ReportGenerationTemplateViewModel>((template) => {
    const inAuthorityScope = templateMatchesAuthorityScope(template);
    return {
      ...template,
      accessible: template.accessible && inAuthorityScope,
      blockedReasons: inAuthorityScope
        ? template.blockedReasons
        : [
            ...template.blockedReasons,
            authorityScope === "all"
              ? "Legacy unverified report templates are hidden by this report authority scope."
              : `Template is outside the ${authorityScope} generated report-field authority.`,
          ],
      selected: input.selectedTemplateId === template.id,
    };
  });

  return {
    state: {
      ...input.state,
      selectedEntities: input.state.selectedEntities.filter((entityId) =>
        accessibleScopedEntityIds.has(entityId),
      ),
      selectedFields: input.state.selectedFields.filter((fieldId) =>
        availableFieldIds.has(fieldId),
      ),
      conditions: input.state.conditions.filter((condition) =>
        availableFieldIds.has(condition.fieldId),
      ),
      sorts: input.state.sorts.filter((sort) =>
        availableFieldIds.has(sort.fieldId),
      ),
    },
    activeFilterCount: deriveReportGenerationActiveFilterCount(input.state),
    canRunReport:
      selectedInaccessibleEntityIds.length === 0 &&
      input.state.selectedEntities.some((entityId) =>
        accessibleScopedEntityIds.has(entityId),
      ) &&
      selectedFieldOptions.length > 0,
    joins: deriveReportGenerationJoinIndicators(input.state.selectedEntities),
    availableFields,
    selectedFieldOptions,
    modules: Object.values(modules).map((module) => ({
      ...module,
      entities: module.entities.sort((left, right) =>
        left.label.localeCompare(right.label),
      ),
    })),
    inaccessibleEntities,
    templates: {
      available: templateViewModels.filter((template) => template.accessible),
      blocked: templateViewModels.filter((template) => !template.accessible),
    },
  };
};
